import React, { useCallback } from "react";
import { Chip, Divider, IconButton } from "@mui/material";
import { BaseInput } from "components/core";
import { Error } from "components/PlanSetup/styles";
import AddIcon from "assets/svgs/add_circle.svg";
import { colors } from "theme";
import { RemoveCircleOutline } from "@mui/icons-material";
import { FieldArray } from "formik";
import * as Yup from "yup";
import { Ptag } from "components/core/fonts";

// Yup Validation Schema Exported
export const escalationValidationSchema = Yup.object().shape({
  escalation_details: Yup.array().of(
    Yup.array().of(
      Yup.object().shape({
        escalation_contact_name: Yup.string().required("Name is required"),
        escalation_contact_email: Yup.string()
          .email("Invalid email")
          .required("Email is required"),
        escalation_contact: Yup.string()
          .matches(/^\d{10,16}$/, "Contact must be 10 to 16 digits")
          .required("Contact is required"),
        escalation_region: Yup.string().nullable(),
      })
    )
  ),
});

const TpaEscalation = ({ formikProps, index }) => {
  
  // Custom onChange handler for phone fields that filters input immediately
  const handlePhoneChange = useCallback((e) => {
    const input = e.target;
    const rawValue = input.value;
    const fieldName = input.name;
    
    // Extract only digits and limit to 16
    const numericValue = rawValue.replace(/\D/g, '').slice(0, 16);
    
    // Update the DOM input value immediately for instant feedback
    input.value = numericValue;
    
    // Create synthetic event for Formik
    const syntheticEvent = {
      target: {
        name: fieldName,
        value: numericValue
      }
    };
    
    // Update Formik state
    formikProps.handleChange(syntheticEvent);
  }, [formikProps]);

  // Prevent non-numeric keys from being typed
  const handlePhoneKeyPress = useCallback((e) => {
    // Allow essential control keys
    const controlKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];
    
    // Allow Ctrl/Cmd shortcuts (copy, paste, select all, etc.)
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    
    // Allow control keys
    if (controlKeys.includes(e.key)) {
      return;
    }
    
    // Only allow digits 0-9, block everything else
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  // Handle paste events to filter non-numeric content
  const handlePhonePaste = useCallback((e) => {
    e.preventDefault();
    
    // Get clipboard data
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('text');
    
    // Extract only digits and limit to 16
    const numericValue = pastedText.replace(/\D/g, '').slice(0, 16);
    
    const input = e.target;
    const fieldName = input.name;
    
    // Update input value directly
    input.value = numericValue;
    
    // Create synthetic event for Formik
    const syntheticEvent = {
      target: {
        name: fieldName,
        value: numericValue
      }
    };
    
    // Update Formik
    formikProps.handleChange(syntheticEvent);
  }, [formikProps]);

  // Handle input event as additional safety net
  const handlePhoneInput = useCallback((e) => {
    const input = e.target;
    let value = input.value;
    
    // Remove all non-digit characters
    const numericValue = value.replace(/\D/g, '');
    
    // Limit to 16 digits
    const limitedValue = numericValue.slice(0, 16);
    
    // Update the input value directly if needed
    if (input.value !== limitedValue) {
      input.value = limitedValue;
      
      // Update formik
      formikProps.setFieldValue(input.name, limitedValue);
    }
  }, [formikProps]);

  const addSegment = (name, label, sub_index) => {
    const isContactField = name === "escalation_contact";
    const isNameField = name === "escalation_contact_name";
    const fieldName = `escalation_details.${index}.${sub_index}.${name}`;
    
    // Handle name field key press to only allow letters and spaces
    const handleNameKeyPress = (e) => {
      // Allow essential control keys
      const controlKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];
      
      // Allow Ctrl/Cmd shortcuts (copy, paste, select all, etc.)
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      
      // Allow control keys
      if (controlKeys.includes(e.key)) {
        return;
      }
      
      // Only allow letters and spaces for name fields
      if (isNameField && !/^[a-zA-Z\s]$/.test(e.key)) {
        e.preventDefault();
      }
    };

    // Handle name field paste to filter out special characters and numbers
    const handleNamePaste = (e) => {
      if (!isNameField) return;
      
      e.preventDefault();
      
      // Get clipboard data
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text');
      
      // Extract only letters and spaces
      const cleanValue = pastedText.replace(/[^a-zA-Z\s]/g, '');
      
      const input = e.target;
      const fieldName = input.name;
      
      // Update input value directly
      input.value = cleanValue;
      
      // Create synthetic event for Formik
      const syntheticEvent = {
        target: {
          name: fieldName,
          value: cleanValue
        }
      };
      
      // Update Formik
      formikProps.handleChange(syntheticEvent);
    };
    
    if (isContactField) {
      return (
        <div style={{ marginBottom: "3rem", position: "relative" }}>
          <BaseInput
            staticLabel={label}
            type="tel"
            fontSize="16px !important"
            formikProps={{
              ...formikProps,
              name: fieldName,
              // Override the handleChange for phone fields
              handleChange: handlePhoneChange
            }}
            // Pass event handlers directly as props
            onKeyDown={handlePhoneKeyPress}
            onPaste={handlePhonePaste}
            onInput={handlePhoneInput}
            inputProps={{
              maxLength: 16,
              pattern: "[0-9]*",
              inputMode: "numeric",
              autoComplete: "tel",
              // Additional styling to hide spinners
              style: { 
                MozAppearance: 'textfield'
              }
            }}
            // Additional CSS to hide webkit spinners
            style={{
              '& input[type=number]::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '& input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              }
            }}
          />
          {formikProps.errors &&
            formikProps.errors["escalation_details"] &&
            formikProps.errors["escalation_details"][index] &&
            formikProps.errors["escalation_details"][index][sub_index] &&
            formikProps.errors["escalation_details"][index][sub_index][name] && (
              <Error>
                {formikProps.errors["escalation_details"][index][sub_index][name]}
              </Error>
            )}
        </div>
      );
    }

    // Regular input for non-phone fields
    return (
      <div style={{ marginBottom: "3rem", position: "relative" }}>
        <BaseInput
          staticLabel={label}
          type={name === "escalation_contact_email" ? "email" : "text"}
          fontSize="16px !important"
          formikProps={{
            ...formikProps,
            name: fieldName,
          }}
          onKeyDown={isNameField ? handleNameKeyPress : undefined}
          onPaste={isNameField ? handleNamePaste : undefined}
        />
        {formikProps.errors &&
          formikProps.errors["escalation_details"] &&
          formikProps.errors["escalation_details"][index] &&
          formikProps.errors["escalation_details"][index][sub_index] &&
          formikProps.errors["escalation_details"][index][sub_index][name] && (
            <Error>
              {formikProps.errors["escalation_details"][index][sub_index][name]}
            </Error>
          )}
      </div>
    );
  };

  return (
    <section style={{ marginBottom: "3rem" }}>
      <Chip
        label={`Level ${index + 1}`}
        sx={{
          borderRadius: "5px",
          fontSize: "12px",
          backgroundColor: "#452d84",
          color: "white",
          marginBottom: "1.4rem",
        }}
      />
      <FieldArray
        name={`escalation_details[${index}]`}
        render={({ push, pop }) => (
          <div>
            {formikProps?.values?.escalation_details[index].map(
              (_, sub_index, array) => (
                <div key={sub_index}>
                  {addSegment("escalation_contact_name", "Name", sub_index)}
                  {addSegment("escalation_contact_email", "Email", sub_index)}
                  {addSegment("escalation_contact", "Contact", sub_index)}
                  {addSegment("escalation_region", "Region (optional)", sub_index)}
                  {sub_index < array.length - 1 && (
                    <div
                      style={{
                        borderTop: `1px dashed ${colors.primaryColor}`,
                        margin: "4rem 0 3rem 0",
                      }}
                    />
                  )}
                </div>
              )
            )}
            {formikProps?.values?.escalation_details[index]?.length > 1 && (
              <IconButton sx={{ borderRadius: "5px" }} onClick={() => pop()}>
                <RemoveCircleOutline
                  style={{
                    marginRight: "0.5rem",
                    height: "22px",
                    width: "22px",
                    color: "#DE6944",
                  }}
                />
                <Ptag style={{ color: "#DE6944", fontSize: "14px", textDecoration: "underline" }}>
                  Remove Contact
                </Ptag>
              </IconButton>
            )}
            <IconButton
              sx={{ borderRadius: "5px" }}
              onClick={() =>
                push({
                  escalation_contact_name: "",
                  escalation_contact_email: "",
                  escalation_contact: "",
                  escalation_region: "",
                })
              }
            >
              <img src={AddIcon} alt="" style={{ marginRight: "1rem" }} />
              <u style={{ color: "#002060", fontSize: "16px" }}>
                <Ptag fontSize={'14px'} color="#002060">
                  Add Contact
                </Ptag>
              </u>
            </IconButton>
            <Divider sx={{ mt: "24px", mb: "10px", backgroundColor: "" }} />
          </div>
        )}
      />
    </section>
  );
};

export default TpaEscalation;